class CompareUsSlider extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement | null} */
    this.wrapper = null;
    /** @type {HTMLElement | null} */
    this.controls = null;
    /** @type {HTMLButtonElement | null} */
    this.prevBtn = null;
    /** @type {HTMLButtonElement | null} */
    this.nextBtn = null;
    /** @type {HTMLElement | null} */
    this.track = null;
    /** @type {HTMLElement | null} */
    this.thumb = null;

    /** Pending requestAnimationFrame id for the eased scroll. @type {number | null} */
    this.animFrame = null;
    /** Scroll position the eased animation is heading towards. @type {number} */
    this.targetScroll = 0;
    /** Animation duration in ms. @type {number} */
    this.duration = 480;

    // Thumb drag state.
    this.dragging = false;
    this.dragStartX = 0;
    this.dragStartScroll = 0;

    // Bind handlers once so add/removeEventListener share the same reference.
    this.updateUI = this.updateUI.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.handlePrev = () => this.scrollByStep(-1);
    this.handleNext = () => this.scrollByStep(1);
  }

  connectedCallback() {
    this.wrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.compare-us__table-wrapper')
    );
    this.controls = /** @type {HTMLElement | null} */ (
      this.querySelector('.compare-us__controls')
    );
    if (!this.wrapper || !this.controls || this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.prevBtn = /** @type {HTMLButtonElement | null} */ (
      this.controls.querySelector('.compare-us__arrow--prev')
    );
    this.nextBtn = /** @type {HTMLButtonElement | null} */ (
      this.controls.querySelector('.compare-us__arrow--next')
    );
    this.track = /** @type {HTMLElement | null} */ (
      this.controls.querySelector('.compare-us__progress')
    );
    this.thumb = /** @type {HTMLElement | null} */ (
      this.controls.querySelector('.compare-us__progress-thumb')
    );

    this.targetScroll = this.wrapper.scrollLeft;

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext);
    this.wrapper.addEventListener('scroll', this.updateUI, { passive: true });
    window.addEventListener('resize', this.updateUI);

    if (this.thumb) this.thumb.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);

    // Initial paint once layout has settled.
    requestAnimationFrame(this.updateUI);
  }

  disconnectedCallback() {
    this.cancelAnim();
    window.removeEventListener('resize', this.updateUI);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }

  /** @returns {number} */
  maxScroll() {
    if (!this.wrapper) return 0;
    return this.wrapper.scrollWidth - this.wrapper.clientWidth;
  }

  /**
   * Show the controls only when the table actually overflows.
   * @returns {boolean}
   */
  refreshVisibility() {
    if (!this.controls) return false;
    const hasOverflow = this.maxScroll() > 1;
    this.controls.style.display = hasOverflow ? '' : 'none';
    return hasOverflow;
  }

  /** Sync the thumb size/position and the arrow disabled states to scrollLeft. */
  updateUI() {
    if (!this.wrapper || !this.track || !this.thumb || !this.prevBtn || !this.nextBtn) return;
    if (!this.refreshVisibility()) return;

    const max = this.maxScroll();
    const ratioVisible = this.wrapper.clientWidth / this.wrapper.scrollWidth;
    const trackWidth = this.track.clientWidth;
    const thumbWidth = Math.max(trackWidth * ratioVisible, 24);
    const progress = max > 0 ? this.wrapper.scrollLeft / max : 0;
    const thumbLeft = (trackWidth - thumbWidth) * progress;

    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${thumbLeft}px)`;

    this.prevBtn.disabled = this.wrapper.scrollLeft <= 1;
    this.nextBtn.disabled = this.wrapper.scrollLeft >= max - 1;
  }

  /**
   * Width of a single comparison column — each arrow click advances one column.
   * @returns {number}
   */
  columnStep() {
    if (!this.wrapper) return 0;
    const col = this.wrapper.querySelector('.compare-us__th:not(.compare-us__th--feature)');
    const width = col ? col.getBoundingClientRect().width : 0;
    return width > 0 ? width : this.wrapper.clientWidth * 0.5;
  }

  /**
   * @param {number} t
   * @returns {number}
   */
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  cancelAnim() {
    if (this.animFrame !== null) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = null;
    }
  }

  /**
   * Eased scroll to an absolute scrollLeft (smoother than native 'smooth').
   * @param {number} to
   */
  animateScrollTo(to) {
    if (!this.wrapper) return;
    this.cancelAnim();

    const wrapper = this.wrapper;
    const from = wrapper.scrollLeft;
    const distance = to - from;
    if (Math.abs(distance) < 1) {
      wrapper.scrollLeft = to;
      return;
    }

    /** @type {number | null} */
    let startTime = null;

    /** @param {number} now */
    const frame = (now) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const t = Math.min(elapsed / this.duration, 1);
      wrapper.scrollLeft = from + distance * this.easeInOutCubic(t);
      if (t < 1) {
        this.animFrame = requestAnimationFrame(frame);
      } else {
        this.animFrame = null;
      }
    };

    this.animFrame = requestAnimationFrame(frame);
  }

  /**
   * @param {number} value
   * @returns {number}
   */
  clampScroll(value) {
    return Math.max(0, Math.min(value, this.maxScroll()));
  }

  /**
   * Advance one column; chained clicks accumulate onto the pending target.
   * @param {number} direction
   */
  scrollByStep(direction) {
    if (!this.wrapper) return;
    const base = this.animFrame !== null ? this.targetScroll : this.wrapper.scrollLeft;
    this.targetScroll = this.clampScroll(base + direction * this.columnStep());
    this.animateScrollTo(this.targetScroll);
  }

  /** @param {PointerEvent} event */
  onPointerDown(event) {
    if (!this.wrapper || !this.thumb) return;
    this.cancelAnim(); // let the drag take over from any running animation
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.wrapper.scrollLeft;
    this.thumb.classList.add('is-dragging');
    if (this.thumb.setPointerCapture && event.pointerId !== undefined) {
      this.thumb.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
  }

  /** @param {PointerEvent} event */
  onPointerMove(event) {
    if (!this.dragging || !this.wrapper || !this.track || !this.thumb) return;
    const trackWidth = this.track.clientWidth;
    const thumbWidth = this.thumb.offsetWidth;
    const travel = trackWidth - thumbWidth;
    if (travel <= 0) return;
    const deltaPx = event.clientX - this.dragStartX;
    const deltaScroll = (deltaPx / travel) * this.maxScroll();
    this.wrapper.scrollLeft = this.dragStartScroll + deltaScroll;
  }

  onPointerUp() {
    if (!this.dragging || !this.wrapper || !this.thumb) return;
    this.dragging = false;
    this.targetScroll = this.wrapper.scrollLeft; // keep the pending target in sync after a drag
    this.thumb.classList.remove('is-dragging');
  }
}

if (!customElements.get('compare-us-slider')) {
  customElements.define('compare-us-slider', CompareUsSlider);
}
